import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { axiosInstance, BASE_URL } from "../../Config";
import "@fancyapps/fancybox/dist/jquery.fancybox.css";
import "@fancyapps/fancybox";

const IntermediateProducts = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const [dateFilter, setDateFilter] = useState("all");

  const dateFilters = [
    { value: "all", label: "All Data" },
    { value: "7", label: "Last 7 Days" },
    { value: "30", label: "This Month" },
    { value: "180", label: "Last 6 Months" },
    { value: "365", label: "Last Year" },
  ];

  const approvalOptions = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Approved" },
    { value: 2, label: "Reject" },
  ];

  const getApprovalStatusInfo = (status) => {
    const value = Number(status ?? 0);
    if (value === 1) return { label: "Approved", badgeClass: "bg-success" };
    if (value === 2) return { label: "Reject", badgeClass: "bg-danger" };
    return { label: "Pending", badgeClass: "bg-warning text-dark" };
  };

  const getAvailableApprovalOptions = (currentStatus) => {
    const status = Number(currentStatus ?? 0);
    if (status === 1 || status === 2) {
      return approvalOptions.filter((opt) => opt.value !== 0);
    }
    return approvalOptions;
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (dateFilter && dateFilter !== 'all') params.append('dateFilter', dateFilter);
      if (currentPage > 1) params.append('page', currentPage);
      params.append('limit', '10');

      const queryString = params.toString();
      const url = queryString ? `/getintermediateproducts?${queryString}` : `/getintermediateproducts`;

      const response = await axiosInstance.get(url);

      if (response.data.success) {
        setProducts(response.data.body?.data || []);
        setTotalPages(response.data.body?.totalPages || 1);
      } else {
        setProducts([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching Intermediary products:", error);
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, dateFilter]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleDateFilter = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  const handleApprovalChange = async (productId, newStatus, currentStatus) => {
    const current = Number(currentStatus);
    const next = Number(newStatus);

    if ((current === 1 || current === 2) && next === 0) {
      toast.error("Approved or rejected product cannot be set back to Pending");
      return;
    }

    try {
      await axiosInstance.post("/product-approval-status", {
        id: productId,
        approvalStatus: Number(newStatus),
      });

      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, approvalStatus: Number(newStatus) }
            : p,
        ),
      );

      if (selectedProduct?.id === productId) {
        setSelectedProduct((prev) => ({
          ...prev,
          approvalStatus: Number(newStatus),
        }));
      }

      toast.success("Approval status updated");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update approval status",
      );
    }
  };

  const deleteProductHandler = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "green",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axiosInstance.delete(`/delete-product/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Product deleted successfully");

        // Refresh the product list with current filters
        fetchProducts();
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting product");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct({
      ...product,
      sessions: product.sessions || [],
    });
  };

  const getCurrentFilterLabel = () => {
    const filter = dateFilters.find((f) => f.value === dateFilter);
    return filter ? filter.label : "All Data";
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const handleChatClick = (product) => {
    const userData = localStorage.getItem("userData");
    if (!userData) {
      toast.error("Please login to start a chat");
      return;
    }

    let currentUser;
    try {
      currentUser = JSON.parse(userData);
    } catch (error) {
      console.error("Error parsing user data:", error);
      toast.error("Invalid user data");
      return;
    }

    const productOwnerId = product.userId || product.ownerId || product.user?.id;

    if (!productOwnerId) {
      console.error("Product data:", product);
      toast.error("Cannot find product owner to chat with");
      return;
    }

    if (currentUser.id === productOwnerId) {
      toast.error("You cannot chat with yourself about your own product");
      return;
    }

    const productOwnerName = product.user?.name ||
                             product.userName ||
                             product.ownerName ||
                             product.User?.name ||
                             "Product Owner";

    const productOwnerImageRaw = product.user?.image ||
                                  product.user?.imageUrl ||
                                  product.ownerImage ||
                                  product.User?.image ||
                                  null;
    const productOwnerImage = productOwnerImageRaw
      ? (productOwnerImageRaw.startsWith('http') ? productOwnerImageRaw : `${BASE_URL}/${productOwnerImageRaw}`)
      : "https://randomuser.me/api/portraits/men/1.jpg";

    const currentUserName = currentUser.name || currentUser.username || "You";
    const currentUserImage = currentUser.image || "https://randomuser.me/api/portraits/men/10.jpg";

    const chatData = {
      chatConstantId: null,
      senderId: currentUser.id,
      receiverId: productOwnerId,
      senderName: productOwnerName,
      senderImage: productOwnerImage,
      originalSenderId: currentUser.id,
      originalReceiverId: productOwnerId,
      lastMessage: "",
      timestamp: new Date().toISOString(),
      formattedTime: "Just now",
      unreadCount: 0,
      isOnline: false,
      messageType: 1,
      lastMessageSenderId: null,
      productId: product.id,
      productRequestId: null,
      productName: product.brandName || product.name || "Product",
      productImage: product.image ? `${BASE_URL}/${product.image}` : null,
      chatType: "product",
    };

    console.log("Navigating to chat with data:", chatData);
    navigate("/chat", { state: { selectedChat: chatData } });
  };

  return (
    <>
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
               <div className="title-box mb-3 pb-1 d-flex justify-content-between align-items-center">
                 <h4 className="mb-0 page-title">Intermediary Products</h4>
                 {/* <Link to="/add-product" className="btn btn-primary">
                   <i className="ri-add-line me-1"></i> Add Product
                 </Link> */}
               </div>
              <div>
                <nav aria-label="breadcrumb" className="mt-1">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard" className="new">
                        <i className="ri-home-4-fill me-1 new" /> Home
                      </Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/" className="new">
                        <i className="ri-group-2-line me-1 new" /> Intermediary Activity
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Intermediary Products
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="card">
                <div className="card-body cusbar">
                  <div
                    className="card-head justify-content-start adjusttwo mb-3"
                    style={{ flexWrap: "nowrap" }}
                  >
                    <div className="tbl-search position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by brand, model..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <i className="ri-search-line" />
                    </div>

                    <div className="dropdown tbl-drop mb-3">
                      <button
                        className="btn btn-light dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {getCurrentFilterLabel()}
                      </button>
                      <ul className="dropdown-menu">
                        {dateFilters.map((filter) => (
                          <li key={filter.value}>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => handleDateFilter(filter.value)}
                            >
                              {filter.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive table-card border-top">
                      <div data-simplebar="" className="cus-scroll scmob">
                        <table className="table table-centered cus-nowrap align-middle hltr mb-0">
                          <thead>
                            <tr>
                              <th>Sr no.</th>
                              {/* <th>Image</th> */}
                              <th>Intermediater</th>
                              <th>Brand Name</th>
                              <th>Model</th>
                              <th>Color</th>
                              <th>Status</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.length > 0 ? (
                              products.map((product, index) => (
                                <tr key={product.id}>
                                  <td>{getSerialNumber(index)}</td>
                                  {/* <td>
                                    {product.image ? (
                                      <a
                                        href={`${BASE_URL}/${product.image}`}
                                        data-fancybox="user-gallery"
                                        data-caption={
                                          product.brandName || "Product Image"
                                        }
                                        className="d-inline-block"
                                      >
                                        <img
                                          src={`${BASE_URL}/${product.image}`}
                                          alt="product"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                              "https://via.placeholder.com/50";
                                          }}
                                        />
                                      </a>
                                    ) : (
                                      <div
                                        style={{
                                          width: "50px",
                                          height: "50px",
                                          borderRadius: "50%",
                                          backgroundColor: "#f8f9fa",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <i className="ri-image-line text-muted"></i>
                                      </div>
                                    )}
                                  </td> */}
                                  <td>{product.user?.name || "N/A"}</td>
                                  <td>{product.brandName || "N/A"}</td>
                                  <td>{product.model || "N/A"}</td>
                                  <td>{product.color || "N/A"}</td>
                                  <td>
                                    <select
                                      className="form-select form-select-sm"
                                      value={product.approvalStatus ?? 0}
                                      onChange={(e) =>
                                        handleApprovalChange(
                                          product.id,
                                          e.target.value,
                                          product.approvalStatus,
                                        )
                                      }
                                      style={{ minWidth: "120px" }}
                                    >
                                      {getAvailableApprovalOptions(
                                        product.approvalStatus,
                                      ).map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <div className="d-flex justify-content-end">
                                      <button
                                        type="button"
                                        className="btn btn-soft-primary px-2 btn-sm me-1"
                                        data-bs-toggle="offcanvas"
                                        data-bs-target="#view-details"
                                        aria-controls="offcanvasRight"
                                        onClick={() =>
                                          handleViewDetails(product)
                                        }
                                        title="View Details"
                                      >
                                        <i className="ri-eye-fill font-size-16"></i>
                                      </button>

                                      <button
                                        onClick={() => handleChatClick(product)}
                                        className="btn btn-soft-success px-2 btn-sm me-1"
                                        style={{
                                          backgroundColor: "#28a745",
                                          borderColor: "#28a745",
                                          color: "#fff",
                                        }}
                                        title="Start Chat"
                                      >
                                        <i className="ri-chat-3-line font-size-16"></i>
                                      </button>

                                      <button
                                        onClick={() =>
                                          deleteProductHandler(product.id)
                                        }
                                        className="btn btn-soft-danger px-2 btn-sm"
                                        style={{
                                          backgroundColor: "#ea5455",
                                          borderColor: "#ea5455",
                                          color: "#fff",
                                        }}
                                        title="Delete product"
                                      >
                                        <i className="ri-delete-bin-line font-size-16"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-2">
                                  <p className="text-muted">No Intermediary products found</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas for Product Details */}
      <div
        className="offcanvas offcanvas-end rdetails"
        tabIndex="-1"
        id="view-details"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="offcanvas-header d-block">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="offcanvas-title mb-0 fw-semibold">Intermediary Product Details</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
        </div>
        <div className="offcanvas-body">
          {selectedProduct ? (
            <>
              <div className="scroll-room mb-3" id="scrollRoom">
                <label className="text-muted small">Image</label>
                {selectedProduct.image ? (
                  <a
                    href={`${BASE_URL}/${selectedProduct.image}`}
                    data-fancybox="gallery"
                    className="image-popup-gallery-item"
                  >
                    <img
                      src={`${BASE_URL}/${selectedProduct.image}`}
                      className="img-fluid rounded"
                      alt="product"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x300";
                      }}
                    />
                  </a>
                ) : (
                  <div
                    className="bg-light rounded d-flex align-items-center justify-content-center"
                    style={{ height: "200px" }}
                  >
                    <i className="ri-user-line font-size-48 text-muted"></i>
                  </div>
                )}
              </div>

              <div className="scroll-room mb-3" id="scrollRoom">
                <label className="text-muted small">Video</label>
                {selectedProduct.video ? (
                  <a
                    href={`${BASE_URL}/${selectedProduct.video}`}
                    data-fancybox="gallery"
                    className="image-popup-gallery-item position-relative d-block"
                  >
                    <img
                      src={`${BASE_URL}/${selectedProduct.thumbnill}`}
                      className="img-fluid rounded"
                      alt="product"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x300";
                      }}
                    />
                    <div
                      className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "rgba(0,0,0,0.6)",
                        borderRadius: "50%",
                      }}
                    >
                      <i
                        className="ri-play-fill text-white"
                        style={{ fontSize: "30px" }}
                      ></i>
                    </div>
                  </a>
                ) : (
                  <div
                    className="bg-light rounded d-flex align-items-center justify-content-center"
                    style={{ height: "200px" }}
                  >
                    <i className="ri-user-line font-size-48 text-muted"></i>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <h6 className="fw-semibold mb-3">Product Information</h6>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small">Product ID</label>
                    <p className="fw-medium">{selectedProduct.id}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small">Status</label>
                    <p className="fw-medium">
                      <span
                        className={`badge ${getApprovalStatusInfo(selectedProduct.approvalStatus).badgeClass}`}
                      >
                        {getApprovalStatusInfo(selectedProduct.approvalStatus).label}
                      </span>
                    </p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Brand Name</label>
                    <p className="fw-medium">{selectedProduct.brandName || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Model</label>
                    <p className="fw-medium">{selectedProduct.model || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Size</label>
                    <p className="fw-medium">{selectedProduct.size || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Color</label>
                    <p className="fw-medium">{selectedProduct.color || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Type</label>
                    <p className="fw-medium">{selectedProduct.type || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Number Of Units Available (Initial)</label>
                    <p className="fw-medium">{selectedProduct.numberOfUnitsAvailableInitial || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Number Of Units Available (SubRequest)</label>
                    <p className="fw-medium">{selectedProduct.numberOfUnitsAvailableSubRequest || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Minimum Units Required For Purchase</label>
                    <p className="fw-medium">{selectedProduct.minimumUnitsRequiredForPurchase || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Unit Price</label>
                    <p className="fw-medium">{selectedProduct.unitPrice || "0"} {selectedProduct.currency || "USD"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Other Information</label>
                    <p className="fw-medium">{selectedProduct.otherinfo || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Shipping Terms</label>
                    <p className="fw-medium">{selectedProduct.shippingTerms || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Payment Terms</label>
                    <p className="fw-medium">{selectedProduct.paymentTerms || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Price Guaranteed Till</label>
                    <p className="fw-medium">{selectedProduct.priceGuaranteedTill || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Origin Country</label>
                    <p className="fw-medium">{selectedProduct.originCountry || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Seller Name</label>
                    <p className="fw-medium">{selectedProduct.user?.name || "N/A"}</p>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Seller Email</label>
                    <p className="fw-medium">{selectedProduct.user?.email || "N/A"}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="ri-user-line font-size-48 text-muted"></i>
              <p className="mt-3 text-muted">No product selected</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IntermediateProducts;
